import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { observer, inject } from 'mobx-react';
import SubscriptionService from '../../api/SubscriptionService';
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  Warning,
  XCircle,
  CreditCard,
} from '@phosphor-icons/react';

const SubscriptionManagement = ({ history, store }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(true);

  // Load subscription status when component mounts
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const subscriptionData =
          await SubscriptionService.getSubscriptionStatus();
        setSubscription(subscriptionData);
      } catch (err) {
        setError(
          'Failed to load subscription information. Please try again later.'
        );
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, []);

  // Handle subscription cancellation
  const handleCancelSubscription = async () => {
    setCancelling(true);

    try {
      // Get cancellation result with additional information
      const result =
        await SubscriptionService.cancelSubscription(cancelAtPeriodEnd);

      // If immediate cancellation or when user will lose access
      if (!cancelAtPeriodEnd) {
        // Store flag for canceled subscription
        sessionStorage.setItem('canceledSubscription', 'true');
      }

      // Refresh subscription status with the new cancel_at_period_end and cancelled_at fields
      const subscriptionData =
        await SubscriptionService.getSubscriptionStatus();
      setSubscription(subscriptionData);

      // Close dialog
      setCancelDialogOpen(false);
    } catch (err) {
      setError('Failed to cancel subscription. Please try again later.');
      console.error(err);
    } finally {
      setCancelling(false);
    }
  };

  // Format dates for display
  const formatDate = dateStr => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center p-5">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-600"></div>
      </div>
    );
  }

  // Get appropriate status icon and color
  const getStatusDisplay = subscription => {
    const { status, cancel_at_period_end } = subscription;

    // Special case for active subscription that will cancel at period end
    if (status === 'active' && cancel_at_period_end) {
      return {
        icon: <Clock size={24} weight="fill" className="text-orange-500" />,
        text: 'Activa (cancelada al final del período)',
        color: 'text-orange-500',
        bg: 'bg-orange-50',
      };
    }

    switch (status) {
      case 'active':
        return {
          icon: (
            <CheckCircle size={24} weight="fill" className="text-green-500" />
          ),
          text: 'Activa',
          color: 'text-green-500',
          bg: 'bg-green-50',
        };
      case 'past_due':
        return {
          icon: <Warning size={24} weight="fill" className="text-yellow-500" />,
          text: 'Pago pendiente',
          color: 'text-yellow-500',
          bg: 'bg-yellow-50',
        };
      case 'canceled':
        return {
          icon: <XCircle size={24} weight="fill" className="text-red-500" />,
          text: 'Cancelada',
          color: 'text-red-500',
          bg: 'bg-red-50',
        };
      case 'trialing':
        return {
          icon: <Clock size={24} weight="fill" className="text-blue-500" />,
          text: 'Período de prueba',
          color: 'text-blue-500',
          bg: 'bg-blue-50',
        };
      default:
        return {
          icon: <XCircle size={24} weight="fill" className="text-gray-500" />,
          text: 'Inactiva',
          color: 'text-gray-500',
          bg: 'bg-gray-50',
        };
    }
  };

  // Get any message from redirect state
  const message = history.location.state?.message || '';

  // Show message based on subscription type
  if (!subscription || !subscription.has_subscription) {
    // Check subscription type
    const isFreeUser = subscription?.subscription_type === 'free_user';
    const isCanceledSubscription =
      subscription?.status === 'canceled' ||
      subscription?.subscription_type === 'canceled' ||
      (subscription?.cancelled_at && !subscription?.cancel_at_period_end) ||
      sessionStorage.getItem('canceledSubscription') === 'true';

    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="mb-4">
          <button
            onClick={() => history.push('/my-profile')}
            className="flex items-center text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft size={20} className="mr-1" />
            <span>Volver a mi perfil</span>
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-medium mb-4">Estado de la suscripción</h2>

          {message && (
            <div className="bg-yellow-50 text-yellow-800 p-4 rounded-md mb-4">
              {message}
            </div>
          )}

          {isFreeUser ? (
            // Free user view
            <div className="bg-blue-50 rounded-md p-5 text-center">
              <CheckCircle size={40} className="mx-auto mb-2 text-blue-500" />
              <p className="text-gray-700 mb-4">
                Tienes una cuenta ilimitada de la plataforma. Tu acceso ha sido
                proporcionado directamente por tu autoescuela.
              </p>
              <div className="bg-white rounded p-3 border border-gray-200 mb-4 text-left">
                <p className="text-sm text-gray-600">
                  <strong>Estado:</strong> Activa (Cuenta ilimitada)
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Organización:</strong>{' '}
                  {subscription?.organization_name ||
                    store.profile.organization ||
                    'No especificada'}
                </p>
              </div>
            </div>
          ) : (
            // Non-free users (canceled or no subscription)
            <div className="bg-gray-50 rounded-md p-5 text-center">
              {isCanceledSubscription ? (
                <>
                  <XCircle size={40} className="mx-auto mb-2 text-red-500" />
                  <p className="text-gray-700 mb-4">
                    Tu suscripción ha sido cancelada. Para continuar utilizando
                    la plataforma, debes renovar tu suscripción.
                  </p>
                </>
              ) : (
                <>
                  <XCircle size={40} className="mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-600 mb-4">
                    No tienes una suscripción activa en este momento.
                  </p>
                </>
              )}

              <button
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                onClick={async () => {
                  try {
                    // Set loading state
                    setLoading(true);

                    // Use the new direct endpoint to get organization code
                    try {
                      const orgData =
                        await SubscriptionService.getCurrentUserOrganizationCode();
                      console.log('Organization code data:', orgData);

                      if (orgData && orgData.organization_code) {
                        // Store the organization code for the subscription setup
                        sessionStorage.setItem(
                          'pendingOrgCode',
                          orgData.organization_code
                        );
                        console.log(
                          'Retrieved organization code:',
                          orgData.organization_code
                        );

                        // Store organization name and subscription amount with fallbacks
                        sessionStorage.setItem(
                          'pendingOrgInfo',
                          JSON.stringify({
                            name:
                              orgData.organization_name ||
                              store.profile.organization ||
                              'Tu organización',
                            amount: orgData.subscription_amount || 0,
                          })
                        );
                      }
                    } catch (error) {
                      console.error('Error fetching organization code:', error);
                      // We'll continue even if this fails - the user will be prompted manually
                    }

                    // Store that we're initiating a new subscription
                    sessionStorage.setItem('pendingSubscription', 'true');
                    // Clear the canceledSubscription flag if it exists
                    sessionStorage.removeItem('canceledSubscription');
                    // Redirect to subscription setup
                    history.push('/student/subscriptions/setup');
                  } catch (error) {
                    console.error(
                      'Error preparing subscription renewal:',
                      error
                    );
                    setError(
                      'Error al preparar la renovación. Por favor, inténtelo de nuevo.'
                    );
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    <span>Preparando...</span>
                  </div>
                ) : isCanceledSubscription ? (
                  'Renovar suscripción'
                ) : (
                  'Iniciar nueva suscripción'
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const statusDisplay = getStatusDisplay(subscription);

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className="mb-4">
        <button
          onClick={() => history.push('/my-profile')}
          className="flex items-center text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft size={20} className="mr-1" />
          <span>Volver a mi perfil</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-medium mb-4">Detalles de la suscripción</h2>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-md mb-4">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {subscription.status === 'active' &&
            subscription.cancel_at_period_end && (
              <div className="bg-orange-50 p-4 rounded-md mb-4">
                <p className="text-orange-800">
                  Tu suscripción ha sido cancelada pero seguirá activa hasta{' '}
                  {formatDate(
                    subscription.access_until || subscription.current_period_end
                  )}
                  .
                </p>
              </div>
            )}

          <div className="flex items-center p-3 rounded-md border border-gray-200">
            <div className="font-medium w-1/3">Organización:</div>
            <div className="flex-1">{subscription.organization_name}</div>
          </div>

          <div className="flex items-center p-3 rounded-md border border-gray-200">
            <div className="font-medium w-1/3">Estado:</div>
            <div className={`flex-1 flex items-center ${statusDisplay.color}`}>
              {statusDisplay.icon}
              <span className="ml-2">{statusDisplay.text}</span>
            </div>
          </div>

          <div className="flex items-center p-3 rounded-md border border-gray-200">
            <div className="font-medium w-1/3">Importe:</div>
            <div className="flex-1">
              {subscription.subscription_amount?.toFixed(2)}€/mes
            </div>
          </div>

          <div className="flex items-center p-3 rounded-md border border-gray-200">
            <div className="font-medium w-1/3">Período actual finaliza:</div>
            <div className="flex-1">
              {formatDate(subscription.current_period_end)}
            </div>
          </div>

          {subscription.cancelled_at && (
            <div className="flex items-center p-3 rounded-md border border-gray-200">
              <div className="font-medium w-1/3">Fecha de cancelación:</div>
              <div className="flex-1">
                {formatDate(subscription.cancelled_at)}
              </div>
            </div>
          )}

          {subscription.cancel_at_period_end && subscription.access_until && (
            <div className="flex items-center p-3 rounded-md border border-gray-200">
              <div className="font-medium w-1/3">Acceso hasta:</div>
              <div className="flex-1">
                {formatDate(subscription.access_until)}
              </div>
            </div>
          )}

          {subscription.payment_method_last4 && (
            <div className="flex items-center p-3 rounded-md border border-gray-200">
              <div className="font-medium w-1/3">Método de pago:</div>
              <div className="flex-1 flex items-center">
                <CreditCard size={20} className="mr-2 text-gray-500" />
                •••• {subscription.payment_method_last4}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 mt-6 pt-6 flex justify-end">
          {subscription.status === 'canceled' ||
          (subscription.cancel_at_period_end && subscription.cancelled_at) ? (
            <div className="flex flex-col items-end">
              <span className="px-4 py-2 bg-gray-100 text-gray-500 rounded-md mb-2">
                {subscription.status === 'canceled'
                  ? 'Suscripción cancelada'
                  : 'Cancelación programada'}
              </span>
              {subscription.status !== 'canceled' &&
                subscription.cancel_at_period_end && (
                  <button
                    className="px-4 py-2 border border-blue-500 text-blue-500 rounded-md hover:bg-blue-50"
                    onClick={async () => {
                      try {
                        setLoading(true);
                        // Store that we're initiating a new subscription
                        sessionStorage.setItem('pendingSubscription', 'true');
                        // Clear the canceledSubscription flag if it exists
                        sessionStorage.removeItem('canceledSubscription');
                        // Redirect to subscription setup
                        history.push('/student/subscriptions/setup');
                      } catch (error) {
                        console.error(
                          'Error preparing subscription renewal:',
                          error
                        );
                        setError(
                          'Error al preparar la renovación. Por favor, inténtelo de nuevo.'
                        );
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                  >
                    Renovar suscripción
                  </button>
                )}
            </div>
          ) : (
            <button
              className="px-4 py-2 border border-red-500 text-red-500 rounded-md hover:bg-red-50"
              onClick={() => setCancelDialogOpen(true)}
            >
              Cancelar suscripción
            </button>
          )}
        </div>
      </div>

      {/* Cancellation Dialog */}
      {cancelDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-medium mb-4">Cancelar suscripción</h3>

            <p className="mb-4">
              ¿Estás seguro de que quieres cancelar tu suscripción a{' '}
              {subscription.organization_name}?
            </p>

            <div className="bg-gray-50 p-4 rounded-md mb-6">
              <h4 className="font-medium mb-2">Opciones de cancelación:</h4>

              <div className="flex items-center mb-3">
                <input
                  type="radio"
                  id="cancel-period-end"
                  checked={cancelAtPeriodEnd}
                  onChange={() => setCancelAtPeriodEnd(true)}
                  className="mr-3"
                />
                <label htmlFor="cancel-period-end">
                  <div className="font-medium">
                    Cancelar al final del período
                  </div>
                  <div className="text-sm text-gray-600">
                    Mantendrás el acceso hasta{' '}
                    {formatDate(subscription.current_period_end)}
                  </div>
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="radio"
                  id="cancel-immediately"
                  checked={!cancelAtPeriodEnd}
                  onChange={() => setCancelAtPeriodEnd(false)}
                  className="mr-3"
                />
                <label htmlFor="cancel-immediately">
                  <div className="font-medium">Cancelar inmediatamente</div>
                  <div className="text-sm text-gray-600">
                    Perderás el acceso de inmediato (reembolso no aplicable)
                  </div>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setCancelDialogOpen(false)}
                disabled={cancelling}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Volver
              </button>

              <button
                onClick={handleCancelSubscription}
                disabled={cancelling}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              >
                {cancelling ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    <span>Procesando...</span>
                  </div>
                ) : (
                  'Confirmar cancelación'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default inject('store')(observer(withRouter(SubscriptionManagement)));
